import React, { useState } from "react";
import { toast } from "sonner";
import { Star, MessageSquare, Reply } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Read-only review card with optional inline "respond" affordance for the
 * reviewed party.
 */
export default function ReviewCard({ review, allowResponse = false, onChange }) {
  const { user } = useAuth();
  const [responding, setResponding] = useState(false);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const isMe = user?.id === review.reviewee_id;

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await api.post(`/reviews/${review.id}/response`, { body });
      toast.success("Response posted");
      onChange?.();
      setResponding(false);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not post response");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#165a71]/10 text-[#165a71] font-display font-semibold flex items-center justify-center overflow-hidden">
          {review.reviewer_avatar_snapshot ? (
            <img src={review.reviewer_avatar_snapshot} alt={review.reviewer_name_snapshot || "reviewer"} className="w-full h-full object-cover" />
          ) : (
            (review.reviewer_name_snapshot || "GC").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("")
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div translate="no" className="font-display font-semibold text-[#071b22]">{review.reviewer_name_snapshot || "Anonymous"}</div>
            <div className="inline-flex items-center gap-1 text-amber-600 text-sm">
              <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
              {(review.overall || 0).toFixed(1)}
            </div>
            {review.verified_transaction && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-medium">verified</span>
            )}
            <span className="text-xs text-[#252A34]/50">{review.created_at ? new Date(review.created_at).toLocaleDateString() : ""}</span>
          </div>
          {review.title && <div translate="no" className="mt-1 font-display text-[#071b22]">{review.title}</div>}
          <div translate="no" className="mt-1 text-sm text-[#252A34] whitespace-pre-line">{review.body}</div>

          {/* Sub-ratings preview */}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#252A34]/70">
            {["quality", "communication", "timeliness", "value", "professionalism", "accuracy", "preparedness", "payment_cooperation", "respectfulness"].map((k) => (
              review[k] ? (
                <span key={k} className="inline-flex items-center gap-1">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  <span className="capitalize">{k.replace(/_/g, " ")}: {review[k]}</span>
                </span>
              ) : null
            ))}
          </div>

          {/* Provider response */}
          {review.response_body && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs uppercase tracking-wider text-[#165a71] font-medium inline-flex items-center gap-1">
                <Reply className="h-3 w-3" /> Response from provider
              </div>
              <div className="text-sm text-[#252A34] mt-1 whitespace-pre-line">{review.response_body}</div>
            </div>
          )}

          {/* Response affordance */}
          {allowResponse && isMe && !review.response_body && (
            !responding ? (
              <button
                onClick={() => setResponding(true)}
                className="mt-3 text-xs font-medium text-[#165a71] inline-flex items-center gap-1"
                data-testid={`review-respond-open-${review.id}`}
              >
                <MessageSquare className="h-3 w-3" /> Respond publicly
              </button>
            ) : (
              <div className="mt-3">
                <textarea
                  rows={3}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="A calm, professional response builds trust."
                  data-testid={`review-response-input-${review.id}`}
                />
                <div className="mt-2 flex gap-2">
                  <button disabled={saving} onClick={submit} className="rounded-full bg-[#165a71] text-white text-xs font-medium px-3 py-1.5" data-testid={`review-response-save-${review.id}`}>{saving ? "Posting…" : "Post response"}</button>
                  <button onClick={() => setResponding(false)} className="rounded-full border border-slate-200 text-xs px-3 py-1.5">Cancel</button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
